import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { PdokAddressDocResult } from '@footsteps/types';
import { addressFormSchema } from '../woz-find-address-modal/woz-find-address-modal.schema';
import { SupabaseApiService } from '../../services/supabase-api';
import type {
  SelectAddressFormValues,
  SelectAddressFormErrors,
  AddressData,
  WozSelectAddressModalState,
} from './woz-select-address-modal.types';

export class WozSelectAddressModalViewModel extends AbstractViewModel<WozSelectAddressModalState> {
  static initialState: WozSelectAddressModalState = {
    postalCode: '',
    houseNumber: '',
    houseNumberAddition: '',
    errors: {},
    isLoading: false,
    apiError: null,
    results: [],
    selectedAddress: null,
    needsSelection: false,
    canConfirm: false,
  };

  constructor() {
    super(WozSelectAddressModalViewModel.initialState);
  }

  setPostalCode = (value: string): void => {
    this.update((state) => {
      state.postalCode = value;
    });
  };

  setHouseNumber = (value: string): void => {
    this.update((state) => {
      state.houseNumber = value;
    });
  };

  setHouseNumberAddition = (value: string): void => {
    this.update((state) => {
      state.houseNumberAddition = value;
    });
  };

  selectAddress = (address: PdokAddressDocResult): void => {
    this.update((state) => {
      state.selectedAddress = address;
      state.canConfirm = true;
    });
  };

  handleSearch = async (): Promise<void> => {
    const { postalCode, houseNumber, houseNumberAddition } = this.state.getValue();

    this.update((state) => {
      state.errors = {};
      state.apiError = null;
      state.selectedAddress = null;
      state.canConfirm = false;
    });

    const parsed = addressFormSchema.safeParse({ postalCode, houseNumber, houseNumberAddition });
    if (!parsed.success) {
      const fieldErrors: SelectAddressFormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof SelectAddressFormValues;
        fieldErrors[field] = issue.message;
      }
      this.update((state) => {
        state.errors = fieldErrors;
      });
      return;
    }

    this.update((state) => {
      state.isLoading = true;
      state.apiError = null;
    });

    try {
      const { postalCode: pc, houseNumber: hn, houseNumberAddition: hna } = parsed.data;
      const api = SupabaseApiService.getInstance();
      const response = await api.lookupAddress(pc, hn, hna || undefined);

      if (response.status === 'not_found' || response.addresses.length === 0) {
        this.update((state) => {
          state.apiError = 'Geen adres gevonden. Controleer de invoer en probeer opnieuw.';
          state.isLoading = false;
          state.results = [];
          state.selectedAddress = null;
          state.needsSelection = false;
          state.canConfirm = false;
        });
        return;
      }

      this.update((state) => {
        state.isLoading = false;
        state.results = response.addresses;
        state.needsSelection = response.addresses.length > 1;
        if (response.addresses.length === 1) {
          state.selectedAddress = response.addresses[0];
          state.canConfirm = true;
        }
      });
    } catch (err) {
      this.update((state) => {
        state.apiError = err instanceof Error ? err.message : 'Er is een fout opgetreden.';
        state.isLoading = false;
        state.results = [];
        state.selectedAddress = null;
        state.needsSelection = false;
        state.canConfirm = false;
      });
    }
  };

  handleConfirm = (onConfirm: (addressData: AddressData) => void): void => {
    const { selectedAddress } = this.state.getValue();
    if (!selectedAddress) return;

    onConfirm({
      addressPointId: selectedAddress.addressPointId ?? selectedAddress.id ?? '',
      addressCity: selectedAddress.city ?? '',
      addressStreet: selectedAddress.street ?? '',
      addressHouseNumber: Number(selectedAddress.houseNumber ?? 0),
      addressHouseLetter: null,
      addressHouseNumberAddition: selectedAddress.houseNumberAddition ?? null,
      addressPostalCode: selectedAddress.postalCode ?? '',
    });
  };

  reset() {
    this.update((state) => {
      Object.assign(state, WozSelectAddressModalViewModel.initialState);
    });
  }
}

export const useWozSelectAddressModalViewModel = () =>
  useViewModel<WozSelectAddressModalState, WozSelectAddressModalViewModel>(() => new WozSelectAddressModalViewModel());
